import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';


// services
import {NGXLogger} from "ngx-logger";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {ApplicationService} from "../../application.service";
import {FacadeService} from "../../../shared/services/facade.service";
import {AnnualIncome} from "../../../shared/models/annual-income";
import {IncomeSource} from "../../../shared/models/income-source";
import {Occupation} from "../../../shared/models/occupation";
import {Country} from "../../../shared/models/country";
import {OnboardingRequest} from "../../../shared/models/onboarding-request.model";
import {ValidationDetail} from "../../../shared/models/ValidationDetail";
import {AppValidator} from "../../../shared/interfaces/AppValidator";
import {CreateApplicationService} from "../create-application.service";

@Component({
  selector: 'app-employment-details',
  templateUrl: './employment-details.component.html',
  styleUrls: ['./employment-details.component.scss']
})
export class EmploymentDetailsComponent implements OnInit, AppValidator, OnDestroy {
  @Input() formGroup!: FormGroup;
  public formElementHasChanged : boolean = false;
  incomeSourceList: IncomeSource[] = [];
  occupationList: Occupation[] = [];
  employerCountryList: Country[] = [];
  annualIncomeList: AnnualIncome[] = [];
  uneditedFormData: any;

  shoppingCartOnboardingRequest: OnboardingRequest;
  validationDetail: ValidationDetail;

  destroySubject$: Subject<void> = new Subject();

  get isPrimary(): boolean {
    return this.formGroup.value.isPrimary;
  }

  constructor(
    private facadeService: FacadeService,
    private logger: NGXLogger,
    private applicationService: ApplicationService,
    private employmentService: CreateApplicationService
  ) {
    this.shoppingCartOnboardingRequest = new OnboardingRequest();
    this.validationDetail = new ValidationDetail(this.formGroup, new Map<string, string>());
  }

  ngOnInit(): void {
    this.populateLists();

    // Listen to posible review data being pushed from verification procedure
    this.applicationService.onboardingRequest$
      .pipe(
        takeUntil(this.destroySubject$)
      ).subscribe(() => {
    });

    this.formGroup.valueChanges.pipe(takeUntil(this.destroySubject$)).subscribe(value => {
      this.formElementHasChanged = true;
    });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    if (!this.formGroup.value.isPrimary) {
      this.clearValidations();
    }
  }


  /***
   * populate the list values
   */
  populateLists(): void {
    this.getEmployerCountryList();
    this.getAnnualIncomeList();
    this.getOccupationList();
    this.getIncomeSourceList();

    this.setupStatusTracker();
    this.setupValidationMap();
    this.setupValidationTrigger();
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    const { applicantType } = this.formGroup.value;
    let labelMap = new Map<string, string>();
    labelMap.set("incomeSource", `${applicantType} Primary Source of Income`);
    labelMap.set("occupation", `${applicantType} Occupation`);
    labelMap.set("employerCountry", `${applicantType} Employer Country`);
    labelMap.set("annualIncome", `${applicantType} Annual Income`);
    this.validationDetail = new ValidationDetail(this.formGroup, labelMap);
  }

  /***
   *  Get status changes for form and update status tracker.
   */
  setupStatusTracker(): void {
    this.formGroup.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(state => {
      let isDirty = this.formGroup.dirty;
      this.logger.debug("Employment Details- Status Change:" + isDirty);
      this.updateValidations();
    });
  }

  setupValidationTrigger(): void {
    this.employmentService.onTriggerValidations.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    });
  };


  /***
   * get the available inccome sources
   */
  getIncomeSourceList(): void {
    // Call the Falcon Service to get income sources
    this.facadeService.getAllIncomeSources().then((incomeSources) => {
      this.incomeSourceList = incomeSources;
      //this.setSourceOfIncomeDesc();
      this.logger.debug("Income Sources = " + this.incomeSourceList);
    });
  }

  /***
   * get the available occupations
   * */
  getOccupationList(): void {
    // Call the Falcon Service to get income sources
    this.facadeService.getAllOccupations().then((occupations) => {
      this.occupationList = occupations;
      this.logger.debug("Occupations = " + this.occupationList);
    });
  }

  //getcountry list
  getEmployerCountryList(): void {
    this.facadeService.getCountries().then(countries => {
      this.employerCountryList = countries;
    });
  }

  getAnnualIncomeList(): void {
    this.facadeService.getAnnualIncomes().then(incomes => {
      this.annualIncomeList = incomes;
      this.annualIncomeList.sort((a,b) => (a.minValue > b.minValue) ? 1 : -1);
    });
  }

  /**
   * Call to update validations in the validations component.
   */
  updateValidations(): void {
    this.employmentService.validationsChange.emit(this.validationDetail);
  }

  clearValidations(): void {
    this.employmentService.validationsChange.emit(
      new ValidationDetail(this.formGroup, this.validationDetail.labels, true)
    );
  }

  compareValueWithSelection(optionValue: any, selectionValue: any): boolean {
    return optionValue === selectionValue;
  }

}
